import React, { createContext, useContext, useState, useCallback } from 'react'
import styled from 'styled-components'

const NotificationContext = createContext(null)

const NotificationContainer = styled.div`
  position: fixed;
  top: 20px;
  right: 20px;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const Notification = styled.div`
  padding: 1rem 1.5rem;
  border-radius: 8px;
  background: ${props => {
    switch (props.$type) {
      case 'success':
        return 'rgba(76, 175, 80, 0.9)';
      case 'error':
        return 'rgba(244, 67, 54, 0.9)';
      case 'warning':
        return 'rgba(255, 152, 0, 0.9)';
      default:
        return 'rgba(33, 150, 243, 0.9)';
    }
  }};
  color: white;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
  display: flex;
  align-items: center;
  justify-content: space-between;
  min-width: 300px;
  max-width: 400px;
  animation: slideIn 0.3s ease forwards;

  @keyframes slideIn {
    from {
      transform: translateX(100%);
      opacity: 0;
    }
    to {
      transform: translateX(0);
      opacity: 1;
    }
  }
`

const CloseButton = styled.button`
  background: none;
  border: none;
  color: white;
  padding: 0;
  margin-left: 1rem;
  cursor: pointer;
  opacity: 0.8;
  transition: opacity 0.2s ease;

  &:hover {
    opacity: 1;
  }

  svg {
    width: 16px;
    height: 16px;
  }
`

const Message = styled.div`
  flex: 1;
`

const Title = styled.div`
  font-weight: 600;
  margin-bottom: 0.25rem;
`

const Content = styled.div`
  font-size: 0.875rem;
  opacity: 0.9;
`

export function NotificationProvider({ children }) {
  const [notifications, setNotifications] = useState([])

  const addNotification = useCallback(({ type = 'info', title, message, duration = 5000 }) => {
    const id = Date.now()
    setNotifications(prev => [...prev, { id, type, title, message }])

    if (duration > 0) {
      setTimeout(() => {
        removeNotification(id)
      }, duration)
    }
  }, [])

  const removeNotification = useCallback((id) => {
    setNotifications(prev => prev.filter(notification => notification.id !== id))
  }, [])

  const value = {
    addNotification,
    removeNotification
  }

  return (
    <NotificationContext.Provider value={value}>
      {children}
      <NotificationContainer>
        {notifications.map(({ id, type, title, message }) => (
          <Notification key={id} $type={type}>
            <Message>
              {title && <Title>{title}</Title>}
              <Content>{message}</Content>
            </Message>
            <CloseButton onClick={() => removeNotification(id)}>
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor">
                <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
              </svg>
            </CloseButton>
          </Notification>
        ))}
      </NotificationContainer>
    </NotificationContext.Provider>
  )
}

export function useNotification() {
  const context = useContext(NotificationContext)
  if (!context) {
    throw new Error('useNotification must be used within a NotificationProvider')
  }
  return context
}

export default NotificationProvider
